import { clientRoot } from '@/proto/notificationProto'
import { useSwapClientStore } from '~/store/swapClient'
import { useUserStore } from '~/store/user'
import { useEventBus } from '@vueuse/core'
import { getDomain } from '@/utils'

let swapClientStore // 直接引用useSwapClientStore会导致 SSR 错误
const clientBaseWsDTO = clientRoot.lookupType('WsBaseRespProto')
const { emit } = useEventBus('swapClientWs')
const dto = {
  orders: clientRoot.lookupType('WsWebOrderRespProto'),
  stopOrder: clientRoot.lookupType('WsWebTriggerOrderRespProto'),
  tradingAccount: clientRoot.lookupType('WsWebAccountRespProto'),
  position: clientRoot.lookupType('WsWebPositionRespProto'),
  indexAccount: clientRoot.lookupType('WsWebIndexAccountDTORespProto'),
  triggerOrder: clientRoot.lookupType('WsWebTriggerOrderRespProto'),
  leverage: clientRoot.lookupType('WsWebLeverageRespProto'),
}

export const formatter = {
  tradingAccount: ({ data }) => {
    swapClientStore ??= useSwapClientStore()
    const { proto = [] } = data
    proto.forEach((i) => {
      swapClientStore.tradingAccount[i.currency] = {
        ...i,
        total: i.balance,
        real: i.marginAvailable,
        trade: Math.max(i.marginAvailable, 0),
        upnl: i.profitUnreal,
        mf: i.marginFrozen,
        mp: i.marginPosition,
      }
    })
  },
  indexAccount: ({ data }) => {
    swapClientStore ??= useSwapClientStore()
    const { proto = [] } = data
    proto.forEach((i) => {
      swapClientStore.indexAccount[i.currency] = {
        ...i,
        total: i.balance,
        real: i.marginAvailable,
        trade: Math.max(i.marginAvailable, 0),
        upnl: i.profitUnreal,
        mm: i.maintenanceMargin,
      }
    })
  },
  leverage: ({ data }) => {
    swapClientStore ??= useSwapClientStore()
    const { proto = [] } = data
    proto.forEach((i) => {
      swapClientStore.leverageInfo[i.tradeType] ??= { [i.symbol]: {} }
      swapClientStore.leverageInfo[i.tradeType][i.symbol] ??= {}
      swapClientStore.leverageInfo[i.tradeType][i.symbol][i.marginType] = i
    })
  },
  position: ({ data }) => {
    swapClientStore ??= useSwapClientStore()
    const { proto = [] } = data
    swapClientStore.positions = proto.filter(i => !!+i.positionAmt)
  },
  orders: ({ data }) => {
    swapClientStore ??= useSwapClientStore()
    const { proto = [] } = data
    const orders = [...swapClientStore.openOrders]

    proto.filter(i => i.orderType === 'limit').forEach((i) => {
      const orderIndex = orders.findIndex(j => j.orderId === i.orderId)
      // 能查到旧订单，按更新时间更新
      if (orderIndex !== -1) {
        const order = orders[orderIndex]
        if (+i.updateTime >= +order.updateTime) {
          if (i.orderStatus === 'partially_filled' || i.orderStatus === 'active') {
            orders.splice(orderIndex, 1, i)
          }
          else {
            orders.splice(orderIndex, 1)
          }
        }
      }
      else {
        if (i.orderStatus === 'partially_filled' || i.orderStatus === 'active') {
          orders.unshift(i)
        }
      }
    })
    swapClientStore.openOrders = orders
  },
  stopOrder: ({ data }) => {
    swapClientStore ??= useSwapClientStore()
    const { proto = [] } = data
    const orders = [...swapClientStore.stopOrders]
    proto.forEach((i) => {
      const orderIndex = orders.findIndex(j => j.stopOrderId === i.stopOrderId)
      // 能查到旧订单，活跃状态则替换
      if (orderIndex !== -1) {
        if (i.status === 2) {
          orders.splice(orderIndex, 1, i)
        }
        else {
          orders.splice(orderIndex, 1)
        }
      }
      else {
        if (i.status === 2) {
          orders.unshift(i)
        }
      }
    })
    swapClientStore.stopOrders = orders
  },
}

function handleMessage(message) {
  const { stream } = message
  const params = { stream, data: dto[stream]?.decode(message.data.value).toJSON() }
  formatter[stream]?.(params)
}
export class WS {
  ws
  timerHb
  timerRc
  _subs = {}
  constructor() {
    if (import.meta.client) {
      this.connect()
    }
  }

  connect(url = `wss://ws-fu.${getDomain()}/notification/v3`) {
    if (this.ws && this.ws.readyState !== 3) {
      this.ws.close()
      this.ws = null
    }
    this.ws = new WebSocket(url)
    this.ws.binaryType = 'arraybuffer'
    this.ws.onopen = this.onopen.bind(this)
    this.ws.onmessage = this.onmessage.bind(this)
    this.ws.onerror = this.onerror.bind(this)
    this.ws.onclose = () => {
      console.log('swapMarketWs closed')
    }
  }

  onopen() {
    this.auth()
    this.reSend()
    this.resetHeartbeat()
  }

  onerror(e) {
    console.log('ws error', e)
    this.timerRc && clearTimeout(this.timerRc)
    this.timerRc = setTimeout(() => this.connect(), 5000)
  }

  auth() {
    const token = localStorage.getItem('token')
    const authMessage = {
      event: 'authorization',
      data: [{
        token,
      }],
    }
    this.ws.send(JSON.stringify(authMessage))
  }

  // 重置websocket心跳
  resetHeartbeat() {
    this.timerHb && clearTimeout(this.timerHb)
    this.timerRc && clearTimeout(this.timerRc)
    // 开启心跳链接, 5秒没有链接则主动pong，10秒没有消息则重连
    this.timerHb = setTimeout(() => {
      this.ping()
    }, 10000)
    // 保持心跳，60秒没有心跳则重连
    this.timerRc = setTimeout(() => {
      this.timerHb && clearTimeout(this.timerHb) // 关闭pong
      // 判断websocket当前状态
      this.connect()
    }, 20000)
  }

  send(msg, cb) {
    if (this.collect(msg, cb))
      return
    if (this.ws?.readyState === 1) {
      const { event, data: [subData] } = msg
      const { tradeType, stream, symbol } = subData
      this.ws?.send(JSON.stringify({
        event,
        data: [
          { tradeType, stream, symbol },
        ],
      }))
    }
  }

  reSend() {
    Object.values(this._subs).forEach((v) => {
      this.ws?.send(JSON.stringify(v))
    })
  }

  subscribe() {
    const subMessage = {
      event: 'subscribe',
      data: 'auth#sub',
    }
    if (this._subs[subMessage.data]) {
      return
    }
    this._subs[subMessage.data] = subMessage
    if (this.ws?.readyState === 1) {
      this.ws.send(JSON.stringify(subMessage))
    }
  }

  unsubscribe(channel, cb) {
    const o = this._subs[channel]
    if (!o)
      return
    this.ws.send(JSON.stringify({
      event: 'unsubscribe',
      data: o.msg.data,
    }))

    this.collectDel(channel, cb)
  }

  ping() {
    this.ws.send('PING')
  }

  onmessage(msg) {
    try {
      const data = msg.data
      if (data instanceof ArrayBuffer) {
        const message = clientBaseWsDTO.decode(new Uint8Array(msg.data))
        handleMessage(message)
      }
      // 每次收到消息后，重置心跳
      this.resetHeartbeat()
    }
    catch (e) {
      console.log('ws onmessage', e)
    }
  }

  collect(msg, cb) {
    const { data: [subData] } = msg
    const channel = subData.stream
    let subed = false
    const o = this._subs

    if (!o[channel]) {
      o[channel] = { msg, cbs: [] }
    }
    else {
      subed = true
    }

    o[channel].msg = msg

    if (cb) {
      const exist = o[msg.channel].cbs.findIndex(i => i === cb) > -1
      if (!exist) {
        o[channel].cbs.push(cb)
      }
    }

    return subed
  }

  collectDel(channel, cb) {
    if (cb) {
      const index = this._subs[channel].cbs.findIndex(i => i === cb)
      this._subs[channel].cbs.splice(index, 1)
    }
    else {
      delete this._subs[channel]
    }
  }
}

export default new WS()
